import React, { useState, useMemo } from "react";
import {
  useAirlinesQuery,
  useDeleteAirlineMutation,
} from "../../services/basicData/basicData";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Container } from "reactstrap";
import { Trans } from "react-i18next";
import ConfirmModal from "../../CommonElements/ConfirmModal";
import { useQueryClient } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  DeleteIcon,
  Edit02Icon,
  AddSquareIcon,
  AirplaneTakeOffIcon,
} from "@hugeicons/core-free-icons";
import { TableC } from "../../CommonElements/TableC";
import BtnAsLink from "../../Component/BtnAsLink";
import { useClientSearch } from "@/utils/hooks/useClientSearch";

// Empty state component for when no airlines are found
const EmptyState = ({ hasSearch }) => {
  const { t } = useTranslation();

  return (
    <div className="empty-state text-center py-5">
      <div className="empty-state-icon mb-3">
        <HugeiconsIcon
          icon={AirplaneTakeOffIcon}
          size={48}
          className="text-muted"
        />
      </div>
      <h4 className="empty-state-title mb-2">{t("NO_AIRLINES_FOUND")}</h4>
      <p className="empty-state-description text-muted mb-4">
        {t("NO_AIRLINES_DESCRIPTION")}
      </p>
      {!hasSearch && (
        <Link to="create" className="btn btn-primary">
          <HugeiconsIcon
            icon={AirplaneTakeOffIcon}
            size={16}
            className="me-2"
          />
          {t("CREATE_FIRST_AIRLINE")}
        </Link>
      )}
    </div>
  );
};

const PAGE_SIZE = 10;

export const Airlines = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    airlineId: null,
  });
  const { data, isLoading, isFetching } = useAirlinesQuery({
    page: currentPage,
    per_page: PAGE_SIZE,
  });
  const airlines = data?.data?.data ?? [];
  const meta = data?.data?.meta ?? data?.meta ?? {};
  const { filteredData, ...restClientSearch } = useClientSearch(airlines);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const loading = isLoading || isFetching;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const pagination =
    meta?.total != null && meta.total > 0
      ? {
          current: meta?.current_page ?? currentPage,
          total: meta.total,
          pageSize: meta?.per_page ?? PAGE_SIZE,
          onChange: handlePageChange,
        }
      : undefined;

  // Delete mutation
  const { mutate: deleteAirline, isPending: isDeleting } =
    useDeleteAirlineMutation();

  // Handle delete modal
  const handleDeleteClick = (airlineId) => {
    setDeleteModalState({ isOpen: true, airlineId });
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({ isOpen: false, airlineId: null });
  };

  const handleDeleteConfirm = () => {
    if (deleteModalState.airlineId) {
      deleteAirline(deleteModalState.airlineId, {
        onSuccess: () => {
          setDeleteModalState({ isOpen: false, airlineId: null });
          queryClient.invalidateQueries({ queryKey: ["airlines"] });
        },
      });
    }
  };

  // Define columns configuration
  const columns = useMemo(
    () => [
      {
        name: t("NAME"),
        key: "name_ar",
        filterable: true,
      },
      {
        name: t("NAME_EN"),
        key: "name_en",
        filterable: true,
      },
      {
        name: t("CODE"),
        key: "code",
        filterable: true,
      },
      { name: t("ACTIONS"), key: "actions", filterable: false },
    ],
    [t]
  );

  // Define actions function
  const getActions = (rowData) => {
    return [
      {
        key: "edit",
        label: (
          <Link
            to={`/app/makkah/basic-data/airlines/${rowData.id}/edit`}
            className="d-flex align-items-center gap-2"
          >
            <HugeiconsIcon size={16} icon={Edit02Icon} />
            {t("EDIT")}
          </Link>
        ),
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        label: (
          <div className="d-flex align-items-center gap-2">
            <HugeiconsIcon size={16} icon={DeleteIcon} />
            {t("DELETE")}
          </div>
        ),
        onClick: () => handleDeleteClick(rowData.id),
        danger: true,
      },
    ];
  };

  // Create button component
  const createButton = (
    <BtnAsLink
      className="btn-primary"
      icon={<HugeiconsIcon icon={AddSquareIcon} />}
      link="create"
      disabled={loading}
    >
      {t("CREATE_AIRLINE")}
    </BtnAsLink>
  );

  return (
    <Container fluid>
      <TableC
        data={filteredData}
        columns={columns}
        {...restClientSearch}
        loading={loading}
        actions={getActions}
        renderActions={createButton}
        emptyState={EmptyState}
        minVisibleColumns={2}
        skeletonCount={3}
        pagination={pagination}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalState.isOpen}
        onToggle={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("CONFIRM")}
        message={
          <Trans
            i18nKey="CONFIRM_DELETE_AIRLINE"
            components={{
              danger: <span className="text-danger" />,
            }}
          />
        }
        dangerText={t("DELETE")}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        isLoading={isDeleting}
        defaultLoadingText={t("DELETING")}
        loadingText={t("DELETING")}
        confirmVariant="danger"
        confirmIcon={DeleteIcon}
      />
    </Container>
  );
};

